// The app's data, generated from .appfactory/product-plan.json.
//
// Regenerated by `appfactory product apply`. Edit the PLAN and re-apply
// rather than editing here, or the next apply will disagree with you.
//
// Deliberately the same shape the chassis shipped: plain `ref`s in a module,
// seeded through `demoData` so /demo shows rows and the real app starts
// empty. A generated app should not introduce a state library its author
// never asked for — and `demoData` is what makes the QA `empty` fixture
// actually produce an empty screen rather than a populated one.

import { ref, computed, watch, toRaw } from 'vue';
import { demoData } from './core/demo.js';
import { records } from './core/storage.js';

// Rows survive a reload.
//
// `core/storage.js` is an IndexedDB record store the chassis has always
// shipped, and the generated store used to ignore it completely — a plain
// `ref([])` that lost everything the moment the page reloaded. An app that
// cannot keep what you typed into it is not working, however well it renders.
//
// Demo rows are NOT persisted. They have negative ids and exist for /demo and
// for QA fixtures; writing them to the real store would put sample customers
// in somebody's actual queue.
const persist = (row) => (Number(row?.id) > 0 ? records.put(toRaw(row)) : null);

// ONE id sequence for every entity, because there is one store.
//
// `records` is a single IndexedDB object store keyed on `id`. Giving each
// entity its own counter starting at 1 meant the first order written
// overwrote the first customer, and removeOrder(1) deleted them — silent
// cross-entity data loss that only appears once an app has two entities.
//
// Seeded from the highest id already stored, so it also survives a reload:
// restarting at 1 would overwrite the first row ever added.
let seq = 0;
const nextId = () => (seq += 1);

// Everything hydrates from ONE read, and nothing may allocate an id before
// it finishes. A row added while this was still in flight took id 1,
// overwrote a stored record, and was then dropped from the screen when the
// hydrated rows replaced the list.
const ready = records.all().then((rows) => {
  seq = rows.reduce((n, r) => Math.max(n, Number(r.id) || 0), 0);
  return rows;
}).catch(() => []);   // no IndexedDB (SSR, private mode) — stay in memory

/** Resolves once stored rows are loaded. Await it before writing. */
export const storeReady = ready;

/** A metropolitan train line as PTV knows it. */
export const lines = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededLine = demoData([
  { id: -1, name: 'Line 1', routeId: 1, colour: 'Sample colour' },
  { id: -2, name: 'Line 2', routeId: 2, colour: 'Sample colour' },
  { id: -3, name: 'Line 3', routeId: 3, colour: 'Sample colour' },
]);
watch(seededLine, (rows) => {
  if (rows.length && !lines.value.length) lines.value = [...rows];
}, { immediate: true });

export const linesReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'line');
  if (mine.length) lines.value = mine;
  return lines.value;
});

export function addLine(input = {}) {
  const row = { id: nextId(), entity: 'line',
    name: input.name ?? '',
    routeId: input.routeId ?? 0,
    colour: input.colour ?? '',
  };
  lines.value = [row, ...lines.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveLine(row) {
  lines.value = lines.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeLine(id) {
  lines.value = lines.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findLine = (id) =>
  lines.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentLine = computed(() => lines.value.slice(0, 3));
export const linesTitle = (row) => row?.name;

/** A line this person wants to hear about. */
export const subscriptions = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededSubscription = demoData([
  { id: -1, line: -1, active: true, quietFrom: 'Sample quietFrom', quietUntil: 'Sample quietUntil', weekdaysOnly: true },
  { id: -2, line: -2, active: false, quietFrom: 'Sample quietFrom', quietUntil: 'Sample quietUntil', weekdaysOnly: false },
  { id: -3, line: -3, active: false, quietFrom: 'Sample quietFrom', quietUntil: 'Sample quietUntil', weekdaysOnly: false },
]);
watch(seededSubscription, (rows) => {
  if (rows.length && !subscriptions.value.length) subscriptions.value = [...rows];
}, { immediate: true });

export const subscriptionsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'subscription');
  if (mine.length) subscriptions.value = mine;
  return subscriptions.value;
});

export function addSubscription(input = {}) {
  const row = { id: nextId(), entity: 'subscription',
    line: input.line ?? '',
    active: input.active ?? false,
    quietFrom: input.quietFrom ?? '',
    quietUntil: input.quietUntil ?? '',
    weekdaysOnly: input.weekdaysOnly ?? false,
  };
  subscriptions.value = [row, ...subscriptions.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveSubscription(row) {
  subscriptions.value = subscriptions.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeSubscription(id) {
  subscriptions.value = subscriptions.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findSubscription = (id) =>
  subscriptions.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentSubscription = computed(() => subscriptions.value.slice(0, 3));
export const subscriptionsTitle = (row) => row?.quietFrom;

/** One disruption from /v3/disruptions, cached server-side. */
export const disruptions = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededDisruption = demoData([
  { id: -1, ptvId: 1, title: 'Disruption 1', description: 'A short description for Disruption 1, long enough to show wrapping.', line: -1, status: 'current', startedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString(), seenAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString(), url: 'https://example.com/url/1' },
  { id: -2, ptvId: 2, title: 'Disruption 2', description: 'A short description for Disruption 2, long enough to show wrapping.', line: -2, status: 'planned', startedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString(), seenAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString(), url: 'https://example.com/url/2' },
  { id: -3, ptvId: 3, title: 'Disruption 3', description: 'A short description for Disruption 3, long enough to show wrapping.', line: -3, status: 'current', startedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString(), seenAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString(), url: 'https://example.com/url/3' },
]);
watch(seededDisruption, (rows) => {
  if (rows.length && !disruptions.value.length) disruptions.value = [...rows];
}, { immediate: true });

export const disruptionsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'disruption');
  if (mine.length) disruptions.value = mine;
  return disruptions.value;
});

export function addDisruption(input = {}) {
  const row = { id: nextId(), entity: 'disruption',
    ptvId: input.ptvId ?? 0,
    title: input.title ?? '',
    description: input.description ?? '',
    line: input.line ?? '',
    status: input.status ?? '',
    startedAt: input.startedAt ?? new Date().toISOString(),
    seenAt: input.seenAt ?? new Date().toISOString(),
    url: input.url ?? '',
  };
  disruptions.value = [row, ...disruptions.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveDisruption(row) {
  disruptions.value = disruptions.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeDisruption(id) {
  disruptions.value = disruptions.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findDisruption = (id) =>
  disruptions.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentDisruption = computed(() => disruptions.value.slice(0, 3));
export const disruptionsTitle = (row) => row?.title;

/** A push registration for this install. */
export const devices = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededDevice = demoData([
  { id: -1, token: 'Sample token', platform: 'ios', pushEnabled: true, registeredAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString() },
  { id: -2, token: 'Sample token', platform: 'android', pushEnabled: false, registeredAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString() },
  { id: -3, token: 'Sample token', platform: 'ios', pushEnabled: false, registeredAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString() },
]);
watch(seededDevice, (rows) => {
  if (rows.length && !devices.value.length) devices.value = [...rows];
}, { immediate: true });

export const devicesReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'device');
  if (mine.length) devices.value = mine;
  return devices.value;
});

export function addDevice(input = {}) {
  const row = { id: nextId(), entity: 'device',
    token: input.token ?? '',
    platform: input.platform ?? '',
    pushEnabled: input.pushEnabled ?? false,
    registeredAt: input.registeredAt ?? new Date().toISOString(),
  };
  devices.value = [row, ...devices.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveDevice(row) {
  devices.value = devices.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeDevice(id) {
  devices.value = devices.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findDevice = (id) =>
  devices.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentDevice = computed(() => devices.value.slice(0, 3));
export const devicesTitle = (row) => row?.token;

/** A metropolitan station, as PTV knows it. Saved so the board opens on the one you actually use. */
export const stations = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededStation = demoData([
  { id: -1, name: 'Station 1', stopId: 1, suburb: 'Sample suburb', lines: 'Sample lines', isDefault: true },
  { id: -2, name: 'Station 2', stopId: 2, suburb: 'Sample suburb', lines: 'Sample lines', isDefault: false },
  { id: -3, name: 'Station 3', stopId: 3, suburb: 'Sample suburb', lines: 'Sample lines', isDefault: false },
]);
watch(seededStation, (rows) => {
  if (rows.length && !stations.value.length) stations.value = [...rows];
}, { immediate: true });

export const stationsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'station');
  if (mine.length) stations.value = mine;
  return stations.value;
});

export function addStation(input = {}) {
  const row = { id: nextId(), entity: 'station',
    name: input.name ?? '',
    stopId: input.stopId ?? 0,
    suburb: input.suburb ?? '',
    lines: input.lines ?? '',
    isDefault: input.isDefault ?? false,
  };
  stations.value = [row, ...stations.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveStation(row) {
  stations.value = stations.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeStation(id) {
  stations.value = stations.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findStation = (id) =>
  stations.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentStation = computed(() => stations.value.slice(0, 3));
export const stationsTitle = (row) => row?.name;
